import { useEffect, useState } from "react";
import Logo from "@/components/brand/Logo";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart, User, ShoppingBag, LayoutDashboard, MessageCircle, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useSiteSettings } from "@/lib/siteSettings";
import AnnouncementBar from "@/components/shop/AnnouncementBar";
import { isStaff } from "@/lib/roles";

export const NAV_LINKS = [
  { label: "Shop", to: "/products" },
  { label: "Benefits", to: "/cold-pressed-coconut-oil-benefits" },
  { label: "Our Farms", to: "/the-farm" },
  { label: "Lab Reports", to: "/lab-reports" },
  { label: "Recipes", to: "/recipes" },
  { label: "Journal", to: "/blog" },
];

/**
 * Global header in the Porsche overlay pattern: Menu on the left, the brand
 * centred, actions on the right; the menu opens a flyout on every screen.
 */
export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const location = useLocation();
  const { totals, setDrawerOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const { count: wishCount } = useWishlist();
  const cartCount = totals.count;
  const settings = useSiteSettings();
  const hasBar = !!(settings?.announcement || "").trim();

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Escape closes the menu; focus moves into it when it opens.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => document.querySelector("[data-testid=mobile-drawer-close]")?.focus(), 50);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [drawerOpen]);

  useEffect(() => {
    if (cartCount === 0) return;
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 500);
    return () => clearTimeout(t);
  }, [cartCount]);

  const icon =
    "h-10 w-10 inline-flex items-center justify-center rounded-control text-brand-bone/80 hover:text-white hover:bg-white/10 transition-colors duration-sm [&_svg]:w-5 [&_svg]:h-5";

  const allLinks = [{ label: "Home", to: "/" }, ...NAV_LINKS, { label: "FAQs", to: "/faqs" }, { label: "Contact", to: "/contact" }];

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed top-0 inset-x-0 z-50 bg-brand-obsidian/95 backdrop-blur-2xl supports-[backdrop-filter]:bg-brand-obsidian/90"
      >
        <AnnouncementBar />
        {/* Porsche style overlay header: menu left, brand centred, actions right. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="flex items-center gap-1">
            <button onClick={() => setDrawerOpen(true)} className="inline-flex items-center gap-2 h-10 pl-2 pr-3 rounded-control text-brand-bone/90 hover:text-white hover:bg-white/10 transition-colors duration-sm [&_svg]:w-5 [&_svg]:h-5" aria-label="Open menu" aria-expanded={drawerOpen} data-testid="nav-menu-btn">
              <Menu /> <span className="hidden sm:inline font-body text-sm">Menu</span>
            </button>
            <nav className="hidden lg:flex items-center" data-testid="nav-links" aria-label="Primary">
              {NAV_LINKS.slice(0, 3).map((link) => (
                <NavLink key={link.to} to={link.to}
                  className={({ isActive }) => cn("h-10 px-3 inline-flex items-center rounded-control font-body text-sm text-brand-bone/75 hover:text-white hover:bg-white/10 transition-colors duration-sm", isActive && "text-white")}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <Link to="/" data-testid="nav-logo" className="justify-self-center text-white font-display text-[19px] tracking-tight" aria-label="Karijeeva home">
            <Logo className="h-7 w-auto" />
          </Link>

          <div className="flex items-center justify-end">
            {settings?.whatsapp_url && (
              <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" aria-label="Talk to us on WhatsApp" data-testid="nav-whatsapp" className={cn(icon, "hidden sm:inline-flex")}>
                <MessageCircle />
              </a>
            )}
            <Link to="/wishlist" aria-label="Wishlist" data-testid="nav-wishlist" className={cn(icon, "hidden sm:inline-flex relative")}>
              <Heart />
              {user && wishCount > 0 && (
                <span data-testid="nav-wishlist-count" className="absolute top-0.5 right-0 min-w-[15px] h-[15px] rounded-full bg-brand-gold text-[9px] font-semibold text-white flex items-center justify-center px-1">
                  {wishCount}
                </span>
              )}
            </Link>
            {isStaff(user) && (
              <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-flex items-center gap-1.5 mx-1 h-8 px-3 rounded-control border border-white/20 font-body text-[12px] text-brand-bone/90 hover:text-white hover:border-white/40">
                <LayoutDashboard className="w-3.5 h-3.5" /> Admin
              </Link>
            )}
            <Link to={user ? "/account" : "/login"} aria-label={user ? "Your account" : "Sign in"} data-testid="nav-account" className={icon}>
              <User />
            </Link>
            <button onClick={() => setCartOpen(true)} aria-label={`Basket, ${cartCount} items`} data-testid="nav-cart" className={cn(icon, "relative", pulse && "animate-pulse")}>
              <ShoppingBag />
              {cartCount > 0 && (
                <span data-testid="nav-cart-count" className="absolute top-0.5 right-0 min-w-[15px] h-[15px] rounded-full bg-brand-gold text-[9px] font-semibold text-white flex items-center justify-center px-1">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Menu flyout (drilldown) on every screen size */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.div className="fixed inset-0 z-[60]" data-testid="mobile-drawer" role="dialog" aria-modal="true" aria-label="Menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <button className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-default" aria-label="Close menu" tabIndex={-1} onClick={() => setDrawerOpen(false)} />
            <motion.div
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              className="absolute left-0 top-0 h-full w-full sm:max-w-md bg-brand-obsidian sm:rounded-r-tile overflow-y-auto"
            >
              <div className="flex items-center justify-between px-4 sm:px-6 h-14">
                <button onClick={() => setDrawerOpen(false)} className="inline-flex items-center gap-2 h-10 pl-2 pr-3 rounded-control text-brand-bone/90 hover:text-white hover:bg-white/10 [&_svg]:w-5 [&_svg]:h-5" aria-label="Close menu" data-testid="mobile-drawer-close">
                  <X /> <span className="font-body text-sm">Close</span>
                </button>
                <Logo className="h-6 w-auto" textClassName="text-white font-display text-[17px]" />
              </div>
              <nav className="px-4 sm:px-6 pt-4 pb-10" aria-label="Site">
                <ul>
                  {allLinks.map((link, idx) => (
                    <motion.li key={link.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * idx + 0.1, duration: 0.4 }}>
                      <Link to={link.to}
                        className="group flex items-center justify-between rounded-control px-3 py-3 font-display text-3xl text-brand-bone hover:text-white hover:bg-white/[0.06] transition-colors duration-sm"
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        {link.label}
                        <ChevronRight className="w-5 h-5 opacity-40 transition-transform duration-sm group-hover:translate-x-1 group-hover:opacity-100" />
                      </Link>
                    </motion.li>
                  ))}
                </ul>
                <div className="mt-8 border-t border-white/10 pt-6 grid grid-cols-2 gap-2 font-body text-base text-brand-bone/75">
                  <Link to="/wishlist" className="py-2 hover:text-white">Wishlist</Link>
                  <Link to={user ? "/account" : "/login"} className="py-2 hover:text-white">{user ? "Account" : "Sign in"}</Link>
                  <Link to="/track-order" className="py-2 hover:text-white">Track order</Link>
                  <Link to="/monthly-refill" className="py-2 hover:text-white">Monthly refill</Link>
                  {isStaff(user) && <Link to="/admin" className="py-2 hover:text-white" data-testid="mobile-nav-admin">Admin</Link>}
                  {settings?.whatsapp_url && <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="py-2 hover:text-white">WhatsApp us</a>}
                </div>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={hasBar ? "h-[86px]" : "h-14"} aria-hidden="true" />
    </>
  );
}
